/**
 * The one list of specimen widget ids.
 *
 * This used to live in two places: an allow-list in scripts/lint-content.ts and
 * a switch in src/components/Specimen.tsx. That arrangement catches exactly one
 * of the two ways it can go wrong — an id in a lesson that is in neither list
 * fails the lint, but an id present in the allow-list and MISSING from the
 * switch renders an empty box with no error anywhere. The lesson silently loses
 * a zone and the build stays green.
 *
 * So there is one list, both sides import it, and Specimen.tsx's router is
 * typed against it: adding an id here without handling it there is a
 * compile error.
 *
 * `shared/` is visible to all three TypeScript projects (app, server, node),
 * which is what makes a file importable by both a React component and a build
 * script without a project-reference fight.
 */
// Add an id here ONLY in the same commit that adds its renderer. Specimen.tsx's
// router is a Record keyed by SpecimenId, so an id with no renderer fails the
// typecheck — which is the entire point, and only works if nobody pre-registers
// ids for widgets that do not exist yet.
//
// Still to come: 'run-py' (Pass L3).
export const SPECIMEN_IDS = [
  'whoami',
  'echo',
  'status',
  'sql',
  'search',
  // ── B1 · The Enginehouse ──
  /** Probes the same globals in browser, sandbox Worker, and Workers server. */
  'runtime',
  /** An editable JavaScript sandbox with a hard 2s kill. */
  'run-js',
  /** Curated snippets that expose sync / microtask / macrotask ordering. */
  'loop-trace',
] as const

export type SpecimenId = (typeof SPECIMEN_IDS)[number]

export function isSpecimenId(value: string): value is SpecimenId {
  return (SPECIMEN_IDS as readonly string[]).includes(value)
}
